import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";

export default function Legal({ kind }) {
    const pages = {
        aviso: {
            title: "Aviso Legal",
            subtitle: "Información legal de Gestión Fiscal 347",
            body: <AvisoLegal />,
        },
        privacidad: {
            title: "Política de Privacidad",
            subtitle: "Cómo tratamos tus datos personales",
            body: <Privacidad />,
        },
        cookies: {
            title: "Política de Cookies",
            subtitle: "Uso de cookies en este sitio",
            body: <Cookies />,
        },
    };
    const page = pages[kind];

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-3xl mx-auto w-full px-6 md:px-12 py-16">
                <div className="mb-10 pb-8 border-b border-[var(--gf-border)]">
                    <div className="micro-label mb-3">— Información legal</div>
                    <h1 className="serif text-4xl md:text-5xl">{page.title}</h1>
                    <p className="text-[var(--gf-text-secondary)] mt-2">{page.subtitle}</p>
                    <p className="text-xs text-[var(--gf-text-muted)] mt-4">
                        Última actualización: {new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                </div>
                <article className="prose-legal">{page.body}</article>
                <div className="mt-12 pt-8 border-t border-[var(--gf-border)] flex flex-wrap gap-6 text-sm">
                    <Link to="/aviso-legal" className="hover:text-[var(--gf-gold)]">Aviso Legal</Link>
                    <Link to="/privacidad" className="hover:text-[var(--gf-gold)]">Política de Privacidad</Link>
                    <Link to="/cookies" className="hover:text-[var(--gf-gold)]">Política de Cookies</Link>
                </div>
            </main>
            <Footer />
            <WhatsAppFab />
        </div>
    );
}

const Section = ({ title, children }) => (
    <section className="mb-8">
        <h2 className="serif text-2xl mb-3 mt-6">{title}</h2>
        <div className="text-[var(--gf-text)] leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
);

function AvisoLegal() {
    return (
        <>
            <Section title="1. Titularidad del sitio web">
                <p>
                    De conformidad con la Ley 34/2002, de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE),
                    se informa que el titular del sitio web <span className="mono">gf347.es</span> es:
                </p>
                <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Nombre comercial:</strong> Gestión Fiscal 347</li>
                    <li><strong>Actividad:</strong> Servicios de análisis financiero y asesoría fiscal</li>
                    <li><strong>Email de contacto:</strong> info@gf347.es</li>
                    <li><strong>WhatsApp:</strong> +34 604 080 483</li>
                </ul>
                <p className="text-xs text-[var(--gf-text-muted)]">
                    Los datos fiscales concretos (NIF, dirección registrada) se añadirán aquí cuando estén disponibles.
                </p>
            </Section>

            <Section title="2. Objeto">
                <p>
                    El presente Aviso Legal regula el uso del sitio web y de los servicios ofrecidos a través de él. El acceso y la utilización
                    implican la aceptación plena de estas condiciones.
                </p>
            </Section>

            <Section title="3. Condiciones de uso">
                <p>
                    El usuario se compromete a hacer un uso adecuado de los contenidos y servicios. En particular, se obliga a no utilizarlos para:
                </p>
                <ul className="list-disc ml-5 space-y-1">
                    <li>Actividades ilícitas o contrarias a la buena fe</li>
                    <li>Introducir virus o elementos que puedan dañar el sistema</li>
                    <li>Suplantar la identidad de otros usuarios</li>
                    <li>Reproducir o distribuir contenidos sin autorización</li>
                </ul>
            </Section>

            <Section title="4. Propiedad intelectual e industrial">
                <p>
                    Todos los contenidos del sitio (textos, gráficos, logotipos, código) son titularidad de Gestión Fiscal 347 o de terceros
                    que han autorizado su uso. Queda prohibida su reproducción total o parcial sin autorización expresa.
                </p>
            </Section>

            <Section title="5. Exclusión de garantías y responsabilidad">
                <p>
                    Los informes y análisis generados por la plataforma son orientativos y se elaboran a partir de los datos introducidos por el usuario.
                    Gestión Fiscal 347 no garantiza la ausencia de errores en los datos introducidos ni sustituye el criterio profesional de un asesor
                    fiscal colegiado. El usuario asume la responsabilidad sobre las decisiones que tome basándose en estos informes.
                </p>
            </Section>

            <Section title="6. Legislación aplicable y jurisdicción">
                <p>
                    Estas condiciones se rigen por la legislación española. Cualquier controversia se someterá a los juzgados y tribunales del
                    domicilio del titular, salvo que la ley disponga otra cosa.
                </p>
            </Section>
        </>
    );
}

function Privacidad() {
    return (
        <>
            <Section title="1. Responsable del tratamiento">
                <p>
                    El responsable del tratamiento de los datos personales recogidos a través de este sitio web es <strong>Gestión Fiscal 347</strong>,
                    con email de contacto <span className="mono">info@gf347.es</span>.
                </p>
            </Section>

            <Section title="2. Datos que recopilamos">
                <p>Recopilamos las siguientes categorías de datos:</p>
                <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Datos de cuenta:</strong> nombre, email, contraseña cifrada</li>
                    <li><strong>Datos fiscales (opcionales):</strong> razón social, NIF, dirección para emitir facturas</li>
                    <li><strong>Datos financieros:</strong> balances de empresas introducidos para su análisis</li>
                    <li><strong>Datos de pago:</strong> procesados por <strong>Stripe</strong>. No almacenamos datos de tarjeta</li>
                    <li><strong>Datos técnicos:</strong> dirección IP, navegador, cookies (ver Política de Cookies)</li>
                </ul>
            </Section>

            <Section title="3. Finalidad del tratamiento">
                <ul className="list-disc ml-5 space-y-1">
                    <li>Prestar los servicios de análisis financiero contratados</li>
                    <li>Gestionar tu cuenta de usuario</li>
                    <li>Emitir facturas y cumplir obligaciones fiscales</li>
                    <li>Enviar comunicaciones relacionadas con el servicio (no promocionales sin consentimiento)</li>
                    <li>Mejorar la calidad del servicio</li>
                </ul>
            </Section>

            <Section title="4. Base legal">
                <p>
                    El tratamiento se basa en la ejecución del contrato de servicios, el cumplimiento de obligaciones legales
                    (facturación, AEAT) y tu consentimiento expreso al registrarte.
                </p>
            </Section>

            <Section title="5. Conservación de los datos">
                <p>
                    Los datos se conservan mientras mantengas tu cuenta activa. Las facturas se conservan durante 6 años según la normativa fiscal española.
                    Puedes solicitar la eliminación de tu cuenta en cualquier momento escribiéndonos a <span className="mono">info@gf347.es</span>.
                </p>
            </Section>

            <Section title="6. Destinatarios de los datos">
                <p>Los datos pueden compartirse con los siguientes proveedores (encargados del tratamiento):</p>
                <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Stripe</strong> (procesamiento de pagos) — EEUU, con garantías DPF</li>
                    <li><strong>OpenAI</strong> (generación de informes con IA) — EEUU, con cláusulas contractuales tipo</li>
                    <li><strong>Resend</strong> (envío de emails transaccionales) — cuando esté activo</li>
                    <li><strong>MongoDB Atlas</strong> (almacenamiento de datos)</li>
                </ul>
            </Section>

            <Section title="7. Tus derechos">
                <p>Puedes ejercer en cualquier momento los siguientes derechos:</p>
                <ul className="list-disc ml-5 space-y-1">
                    <li>Acceso, rectificación y supresión de tus datos</li>
                    <li>Portabilidad de tus datos</li>
                    <li>Limitación u oposición al tratamiento</li>
                    <li>Revocar el consentimiento en cualquier momento</li>
                    <li>Reclamar ante la <strong>Agencia Española de Protección de Datos</strong> (www.aepd.es)</li>
                </ul>
                <p>Para ejercer estos derechos, escríbenos a <span className="mono">info@gf347.es</span> aportando un documento que acredite tu identidad.</p>
            </Section>

            <Section title="8. Seguridad">
                <p>
                    Aplicamos medidas técnicas y organizativas adecuadas: contraseñas cifradas con bcrypt, comunicaciones HTTPS,
                    autenticación con tokens JWT, copias de seguridad regulares y acceso restringido al personal autorizado.
                </p>
            </Section>
        </>
    );
}

function Cookies() {
    return (
        <>
            <Section title="1. ¿Qué son las cookies?">
                <p>
                    Las cookies son pequeños archivos de texto que los sitios web almacenan en el navegador del usuario con
                    información sobre su visita, preferencias o sesión.
                </p>
            </Section>

            <Section title="2. Cookies que utilizamos">
                <p>Este sitio utiliza únicamente cookies <strong>estrictamente necesarias</strong> para su funcionamiento:</p>
                <div className="border border-[var(--gf-border)] my-4">
                    <table className="w-full text-sm">
                        <thead className="bg-[var(--gf-subtle)]">
                            <tr>
                                <th className="text-left p-3 font-semibold">Cookie</th>
                                <th className="text-left p-3 font-semibold">Finalidad</th>
                                <th className="text-left p-3 font-semibold">Duración</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-t border-[var(--gf-border)]">
                                <td className="p-3 mono text-xs">access_token</td>
                                <td className="p-3">Mantener tu sesión iniciada</td>
                                <td className="p-3">7 días</td>
                            </tr>
                            <tr className="border-t border-[var(--gf-border)]">
                                <td className="p-3 mono text-xs">gf_token</td>
                                <td className="p-3">Token JWT (almacenado en localStorage)</td>
                                <td className="p-3">7 días</td>
                            </tr>
                            <tr className="border-t border-[var(--gf-border)]">
                                <td className="p-3 mono text-xs">gf347_cookie_consent</td>
                                <td className="p-3">Recordar tu aceptación del aviso de cookies</td>
                                <td className="p-3">1 año</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p>No utilizamos cookies de terceros con fines publicitarios ni de análisis de comportamiento.</p>
            </Section>

            <Section title="3. Cómo gestionar las cookies">
                <p>
                    Puedes configurar tu navegador para bloquear cookies o recibir un aviso antes de aceptarlas. Si bloqueas todas las
                    cookies, es posible que no puedas iniciar sesión ni utilizar los servicios.
                </p>
                <ul className="list-disc ml-5 space-y-1 text-sm">
                    <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="underline">Google Chrome</a></li>
                    <li><a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="underline">Mozilla Firefox</a></li>
                    <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="underline">Safari</a></li>
                    <li><a href="https://support.microsoft.com/es-es/help/17442" target="_blank" rel="noopener noreferrer" className="underline">Microsoft Edge</a></li>
                </ul>
            </Section>
        </>
    );
}
